// src/modules/app-views/services/pages/Document/tabs/ParametersTab/components/SuggestedToolsCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Table, Button, Flex, Space, Popconfirm, Tag } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { servicesAddToSet } from '../../../../../service';
import SuggestedToolModal from '../modals/SuggestedToolModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SuggestedToolsCard = ({ data, disabled, onRefresh }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const dataSource = data?.suggestedTools || [];

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleAdd = useCallback(() => {
    setEditingRecord(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback(record => {
    setEditingRecord(record);
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setModalOpen(false);
    setEditingRecord(null);
  }, []);

  const handleSave = useCallback(
    async formData => {
      setLoading(true);
      try {
        const payload = editingRecord
          ? { _id: editingRecord._id, ...formData }
          : formData;

        await servicesAddToSet({
          _id: data._id,
          operator: '$push',
          path: 'suggestedTools',
          data: payload,
        });

        appService.message({ flag: 's', text: 'saved_successfully' });
        setModalOpen(false);
        setEditingRecord(null);
        onRefresh?.();
      } catch (error) {
        console.error('[SuggestedToolsCard] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [data?._id, editingRecord, onRefresh]
  );

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        await servicesAddToSet({
          _id: data._id,
          operator: '$pull',
          path: 'suggestedTools._id',
          data: { _id: record._id },
        });

        appService.message({ flag: 's', text: 'removed_successfully' });
        onRefresh?.();
      } catch (error) {
        console.error('[SuggestedToolsCard] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?._id, onRefresh]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const columns = buildColumns({ disabled, loading, handleEdit, handleDelete });

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <>
      <Card title={<div className="text-muted">{translateX('suggested_tools')}</div>}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
        />

        {!disabled && (
          <Flex justify="flex-end" className="mt-3">
            <Button
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={loading}
            >
              {translateX('add_tool')}
            </Button>
          </Flex>
        )}
      </Card>

      <SuggestedToolModal
        open={modalOpen}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SuggestedToolsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildColumns({ disabled, loading, handleEdit, handleDelete }) {
  const cols = [
    {
      title: translateX('name'),
      dataIndex: 'name',
      ellipsis: true,
      render: text => <strong>{text}</strong>,
    },
    {
      title: translateX('type'),
      dataIndex: 'type',
      width: 140,
      render: type => {
        if (type === 'physical') {
          return <Tag color="blue">{translateX('physical_tool')}</Tag>;
        }
        if (type === 'software') {
          return <Tag color="purple">{translateX('software_tool')}</Tag>;
        }
        return <span className="text-muted">-</span>;
      },
    },
    {
      title: translateX('description'),
      dataIndex: 'description',
      ellipsis: true,
      render: text =>
        text ? (
          <span className="text-muted">{text}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      title: translateX('link'),
      dataIndex: 'link',
      width: 60,
      align: 'center',
      render: (_, record) =>
        record.link ? (
          <a href={record.link} target="_blank" rel="noopener noreferrer">
            <LinkOutlined />
          </a>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
  ];

  // actions column only when not disabled
  if (!disabled) {
    cols.push({
      title: translateX('actions'),
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={loading}
            />
          </Popconfirm>
        </Space>
      ),
    });
  }

  return cols;
}
